import React, { useState, useEffect } from 'react';
import {
  Phone,
  Mail,
  Calendar,
  FileText,
  CheckSquare,
  Clock,
  Trash2,
  CheckCircle,
  Circle,
} from 'lucide-react';
import { getActivitiesByOpportunity, deleteActivity, updateActivity, type Activity } from '../lib/activityService';

interface ActivityTimelineProps {
  opportunityId: string;
  onRefresh?: () => void;
}

const activityIcons = {
  call: Phone,
  email: Mail,
  meeting: Calendar,
  note: FileText,
  task: CheckSquare,
};

const activityColors = {
  call: 'bg-blue-100 text-blue-600',
  email: 'bg-green-100 text-green-600',
  meeting: 'bg-purple-100 text-purple-600',
  note: 'bg-yellow-100 text-yellow-600',
  task: 'bg-orange-100 text-orange-600',
};

export default function ActivityTimeline({ opportunityId, onRefresh }: ActivityTimelineProps) {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadActivities();
  }, [opportunityId]);

  const loadActivities = async () => {
    setLoading(true);
    const data = await getActivitiesByOpportunity(opportunityId);
    setActivities(data);
    setLoading(false);
  };

  const handleDelete = async (activityId: string) => {
    if (confirm('Are you sure you want to delete this activity?')) {
      const success = await deleteActivity(activityId);
      if (success) {
        await loadActivities();
        onRefresh?.();
      }
    }
  };

  const handleToggleComplete = async (activity: Activity) => {
    const success = await updateActivity(activity.id, {
      is_completed: !activity.is_completed,
    });
    if (success) {
      await loadActivities();
      onRefresh?.();
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const formatScheduledDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();

    if (isToday) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }
    if (isTomorrow) {
      return `Tomorrow at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isOverdue = (activity: Activity) => {
    if (activity.activity_type !== 'task' || activity.is_completed || !activity.scheduled_at) {
      return false;
    }
    return new Date(activity.scheduled_at) < new Date();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
        <p className="text-sm">No activities yet</p>
        <p className="text-xs mt-1">Activity history will appear here</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity, index) => {
        const Icon = activityIcons[activity.activity_type];
        const colorClass = activityColors[activity.activity_type];
        const overdue = isOverdue(activity);

        return (
          <div key={activity.id} className="relative">
            {index !== activities.length - 1 && (
              <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gray-200"></div>
            )}

            <div className="flex items-start space-x-4">
              <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${colorClass}`}>
                <Icon className="w-5 h-5" />
              </div>

              <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-semibold text-gray-900">{activity.title}</h4>
                      {activity.activity_type === 'task' && (
                        <button
                          onClick={() => handleToggleComplete(activity)}
                          className={`flex-shrink-0 ${activity.is_completed ? 'text-green-600' : 'text-gray-400 hover:text-green-600'}`}
                          title={activity.is_completed ? 'Mark as incomplete' : 'Mark as complete'}
                        >
                          {activity.is_completed ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <Circle className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>

                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span className="capitalize">{activity.activity_type}</span>
                      <span>•</span>
                      <span>{formatDateTime(activity.activity_date || activity.created_at)}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleDelete(activity.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                    title="Delete activity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {activity.description && (
                  <p className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">{activity.description}</p>
                )}

                <div className="flex flex-wrap gap-3 text-xs">
                  {activity.duration_minutes && (
                    <div className="flex items-center text-gray-600 bg-gray-50 px-2 py-1 rounded">
                      <Clock className="w-3 h-3 mr-1" />
                      <span>{activity.duration_minutes} min</span>
                    </div>
                  )}

                  {activity.scheduled_at && (
                    <div
                      className={`flex items-center px-2 py-1 rounded ${
                        overdue
                          ? 'text-red-600 bg-red-50'
                          : activity.is_completed
                          ? 'text-green-600 bg-green-50'
                          : 'text-blue-600 bg-blue-50'
                      }`}
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      <span>
                        {overdue && 'Overdue: '}
                        {formatScheduledDate(activity.scheduled_at)}
                      </span>
                    </div>
                  )}

                  {activity.is_completed && activity.completed_at && (
                    <div className="flex items-center text-green-600 bg-green-50 px-2 py-1 rounded">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      <span>Completed {formatDateTime(activity.completed_at)}</span>
                    </div>
                  )}
                </div>

                {activity.outcome && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-600 font-medium mb-1">Outcome:</p>
                    <p className="text-sm text-gray-700">{activity.outcome}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
